import {StateEffect, StateField, type Extension} from '@codemirror/state';
import type {
  CompletionContext,
  CompletionResult,
  CompletionSource,
} from '@codemirror/autocomplete';
import {cssLanguage} from '@codemirror/lang-css';
import {htmlLanguage} from '@codemirror/lang-html';
import {javascriptLanguage} from '@codemirror/lang-javascript';
import type {CompletionData} from './types';
import {htmlCompletionSource} from './html-source';
import {cssCompletionSource} from './css-source';
import {jsCompletionSource} from './js-source';

export type {CompletionData, CssClassItem, CssVariableItem} from './types';

const emptyData: CompletionData = {
  cssClasses: [],
  cssVariables: [],
};

/**
 * Effect used to update the completion data at runtime (e.g., when Flux stores change).
 */
export const updateCompletionData = StateEffect.define<CompletionData>();

/**
 * StateField that holds the current completion data. Updated via `updateCompletionData` effect.
 */
export const completionDataField = StateField.define<CompletionData>({
  create: () => emptyData,
  update(value, tr) {
    for (const effect of tr.effects) {
      if (effect.is(updateCompletionData)) {
        return effect.value;
      }
    }
    return value;
  },
});

function makeSource(
  sourceFn: (
    ctx: CompletionContext,
    data: CompletionData
  ) => CompletionResult | null
): CompletionSource {
  return (context: CompletionContext) => {
    const data = context.state.field(completionDataField, false);
    if (
      !data ||
      (data.cssClasses.length === 0 && data.cssVariables.length === 0)
    ) {
      return null;
    }
    return sourceFn(context, data);
  };
}

/**
 * Like makeSource but passes null data when no Webflow-specific data exists.
 * Used for CSS where property-value completions work without site data.
 */
function makeCssSource(
  sourceFn: (
    ctx: CompletionContext,
    data: CompletionData | null
  ) => CompletionResult | null
): CompletionSource {
  return (context: CompletionContext) => {
    const data = context.state.field(completionDataField, false) ?? null;
    const hasData =
      data && (data.cssClasses.length > 0 || data.cssVariables.length > 0);
    return sourceFn(context, hasData ? data : null);
  };
}

const wfHtmlSource = makeSource(htmlCompletionSource);
const wfCssSource = makeCssSource(cssCompletionSource);
const wfJsSource = makeSource(jsCompletionSource);

/**
 * Creates CM6 extensions that provide Webflow-specific autocompletion for CSS
 * classes and CSS variables. Registers completion sources
 * per-language so they fire inside nested language regions (e.g., CSS inside
 * `<style>` in HTML).
 *
 * @param initialData - Optional initial completion data. Can be updated at
 *   runtime by dispatching `updateCompletionData` effects.
 */
export function createCompletionExtension(
  initialData?: CompletionData
): Extension[] {
  return [
    initialData
      ? completionDataField.init(() => initialData)
      : completionDataField,

    htmlLanguage.data.of({autocomplete: wfHtmlSource}),
    cssLanguage.data.of({autocomplete: wfCssSource}),
    javascriptLanguage.data.of({autocomplete: wfJsSource}),
  ];
}
